import { FunctionComponent, useContext, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import { FeedCard, HorizontalStoryFeed, NewsFeedOnboardingModal } from '../../components';
import { ChannelContext, ConfigContext } from '../../context';
import { ChannelAppTabAction, useStoriesByChannelIdQuery } from '../../services';
import {
  CreateCardWithInput,
  FeedCardLoader,
  getLocale,
  NewsFeedCommon,
  useAppSelector,
  useNewsFeed,
} from '../../shared';
import { NoContent } from '../Error';
import { NewsFeedSubTabs } from './NewsFeedSubTabs';

import classes from './NewsFeed.module.scss';

export const NewsFeed: FunctionComponent = () => {
  const { t } = useTranslation();

  const {
    getNewsFeedItems,
    loadMoreNewsFeedItems,
    getSubTabFeedItems,
    loadMoreSubTabFeedItems,
    subTabStoryId,
  } = useNewsFeed();

  const { newsFeed, isFetched } = useAppSelector(({ newsFeed }) => newsFeed);

  const { id: channelId, appTabs } = useContext(ChannelContext).channel;

  const { addContent } = useContext(ConfigContext).config.elements;

  const { showOnNewsFeed, addContentLocaliseKey } = addContent;

  const { data: postInStories, isLoading: isPostInStoriesLoading } = useStoriesByChannelIdQuery(
    { channelId, filter: { onlyPostIn: true } },
    { skip: !showOnNewsFeed }
  );

  useEffect(() => {
    if (subTabStoryId) {
      getSubTabFeedItems();
    } else {
      getNewsFeedItems();
    }
  }, [subTabStoryId, getSubTabFeedItems, getNewsFeedItems]);

  const loadMore = subTabStoryId ? loadMoreSubTabFeedItems : loadMoreNewsFeedItems;

  const newsFeedTitle =
    appTabs
      .find(({ action }) => action.type === ChannelAppTabAction.NEWS_FEED)
      ?.title.find(({ language }) => language === getLocale())?.localization ??
    t('appTabs.NEWS_FEED');

  const hasCards = useMemo(() => Boolean(newsFeed.length), [newsFeed.length]);

  const loader = useMemo(() => <FeedCardLoader />, []);

  const canCreateCard = useMemo(() => {
    if (!showOnNewsFeed || !postInStories?.length) {
      return false;
    }

    if (subTabStoryId) {
      return Boolean(postInStories.find(({ id }) => id === subTabStoryId));
    }

    return true;
  }, [showOnNewsFeed, postInStories, subTabStoryId]);

  const renderCreateCardInput = useMemo(() => {
    if (isPostInStoriesLoading) {
      return <Skeleton height={'4rem'} />;
    }

    if (!canCreateCard) {
      return null;
    }

    return <CreateCardWithInput label={t(addContentLocaliseKey)} />;
  }, [isPostInStoriesLoading, canCreateCard, t, addContentLocaliseKey]);

  const renderContent = useMemo(() => {
    if (!isFetched) {
      return loader;
    }

    if (!hasCards) {
      return <NoContent />;
    }

    return (
      <NewsFeedCommon
        loader={loader}
        loadMore={loadMore}
        HorizontalStoryFeedComponent={HorizontalStoryFeed}
        CardComponent={FeedCard}
      />
    );
  }, [isFetched, hasCards, loader, loadMore]);

  return (
    <div className={classes['news-feed']}>
      <h1 className={classes['news-feed__title']}>{newsFeedTitle}</h1>
      <NewsFeedSubTabs />
      {renderCreateCardInput}
      {renderContent}
      <NewsFeedOnboardingModal />
    </div>
  );
};
